import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withViewTransitions } from '@angular/router';
import { routes } from './app.routes';
import Aura from '@primeuix/themes/aura';
import { OAuthService, OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { providePrimeNG } from 'primeng/config';
import { ColorScale, palette } from '@primeuix/themes';
import { authCodeFlowConfig } from '@v1/modules/auth/infra/auth.config';
import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { EMPTY, from, throwError } from 'rxjs';
import { SessionService } from '@v1/modules/auth/infra/session.service';
import { catchError, switchMap } from 'rxjs/operators';

Aura.semantic = {
  ...Aura.semantic,
  primary: palette('#226cb6') as ColorScale,
};

// Retry one unauthorized bearer request; reauthenticate locally only when credentials cannot recover.
export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const oauth = inject(OAuthService);
  const session = inject(SessionService);
  return next(req).pipe(catchError((error: HttpErrorResponse) => {
    if (error.status !== 401 || !req.headers.has('Authorization') ||
        req.url.includes('/op/token') || req.url.includes('/op/revoke') || req.url.includes('/op/logout')) {
      return throwError(() => error);
    }
    if (!oauth.getRefreshToken()) {
      session.closeLocally();
      return EMPTY;
    }
    return from(session.refresh()).pipe(
      catchError((refreshError: HttpErrorResponse) => {
        if (refreshError.status === 400 || refreshError.status === 401) {
          session.closeLocally();
          return EMPTY;
        }
        return throwError(() => refreshError);
      }),
      switchMap(() => next(req.clone({ setHeaders: { Authorization: `Bearer ${oauth.getAccessToken()}` } })).pipe(
        catchError((retryError: HttpErrorResponse) => {
          if (retryError.status === 401) {
            session.closeLocally();
            return EMPTY;
          }
          return throwError(() => retryError);
        }),
      )),
    );
  }));
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptors([authErrorInterceptor]), withFetch()),
    provideRouter(routes, withComponentInputBinding(), withViewTransitions()),
    providePrimeNG({
      theme: { preset: Aura, options: { darkModeSelector: '.dark' } },
    }),
    provideOAuthClient(),
    { provide: OAuthStorage, useValue: localStorage },
    provideAppInitializer(async () => {
      const oauthService = inject(OAuthService);
      oauthService.configure(authCodeFlowConfig);
      oauthService.setupAutomaticSilentRefresh();

      // ⚠️ Cambiar loadDiscoveryDocumentAndTryLogin por loadDiscoveryDocument
      // await oauthService.loadDiscoveryDocument();
    }),
  ],
};
